/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useHookstate } from "@hookstate/core";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Catalog } from "../../../hooks/useCatalogState";
import { ProductCategory, ProductDepartment } from "../../../hooks/useInventoryState";
import { CategoryControl } from "./CategoryControl";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";
import { FolderClosed, FolderOpen } from "lucide-react";
import { useThemeState } from "@/hooks/useThemeState";


//-------------------------------------------------------------------------------------------------------
export type DepartmentControlProps = {
    catalog: Catalog;
    department: ProductDepartment;
    departments: ProductDepartment[];
}
export const DepartmentControl: React.FC<DepartmentControlProps> = props => {
    const { departmentid, categoryid } = useParams();
    const themeState = useThemeState();
    const mediaQueries = useTailwindMediaQueries();
    // Check if this department is selected based on URL parameters
    const isDepartmentSelected = (departmentid === props.department.Id);
    
    // Auto-expand department if it's selected or has a selected category
    const shouldAutoExpand = Boolean(isDepartmentSelected || 
        (departmentid === props.department.Id && categoryid));
    
    let isFilterActiveState = useHookstate<boolean>(shouldAutoExpand || props.departments.length === 1);
    let isFilterActive = isFilterActiveState.get();
    
    // Auto-expand when department becomes selected
    useEffect(() => {
        if (shouldAutoExpand && !isFilterActive) {
            isFilterActiveState.set(true);
        }
    }, [shouldAutoExpand, isFilterActive, isFilterActiveState]);

    return (
        <React.Fragment key={props.department.Id}>
            <div
                css={css`
                    display:flex;
                    align-items:center;
                    padding:.1rem;
                    cursor: pointer;
                `}
                onClick={async () => {
                    return Promise.resolve().then(() => {
                        isFilterActive = (!isFilterActive);
                        isFilterActiveState.set(isFilterActive);
                    });
                }}>
                <div
                    css={css`
                        flex:1 1 auto;
                        text-indent: -.5em;
                        display: flex;
                        align-items: center;
                    `}>
                    <div css={css`color: ${(themeState.isDark() ? 'inherit' : '#333333')};flex: 0 0 auto;display:flex;align-items:center;`}>
                        {!isFilterActive && (<FolderClosed size={mediaQueries.desktop ? 16 : 22} />)}
                        {isFilterActive && (<FolderOpen size={mediaQueries.desktop ? 16 : 22} />)}
                    </div>
                    <div css={css`color: ${(themeState.isDark() ? 'inherit' : '#333333')};flex: 1 1 0;padding:${mediaQueries.desktop ? '0' : '4px'} 14px;font-size:${mediaQueries.desktop ? '.8rem' : '1rem'};`}>{props.department.Name}</div>
                    {(props.department.hasExpander || (props.department.hasExpander === undefined)) && (
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                flex:0 0 auto;
                            `}>
                            {!isFilterActive &&
                                <KeyboardArrowRight fontSize="small" />
                            }
                            {isFilterActive &&
                                <KeyboardArrowDown fontSize="small" />
                            }
                        </div>
                    )}
                </div>
            </div>
            {isFilterActive && (
                <div css={css`margin:0 0 0 8px;border-left: 1px dotted #aaaaaa;`}>
                    {props.department.Categories && props.department.Categories.map((productCategory: ProductCategory) => (
                        <CategoryControl
                            key={productCategory.Id}
                            catalog={props.catalog}
                            department={props.department}
                            category={productCategory}
                            isParentFilterActive={isFilterActive} />
                    ))}
                </div>
            )}
        </React.Fragment>
    );
}